import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LOADING_STATE } from "types/general";
import { initialState, RESEARCH_SLICE_NAME } from "./constants";
import {
  addWorkItemNote,
  archiveWorkItem,
  assignWorkItem,
  cancelDemand,
  claimWorkItem,
  fetchResearchProductivity,
  fetchResearchClientProgress,
  fetchResearchPropertyHistory,
  fetchResearchReferenceData,
  fetchResearchSummary,
  fetchResearchWorkItemPath,
  fetchResearchWorkItems,
  previewIntake,
  restoreDemand,
  restoreWorkItem,
  submitIntake,
  transitionWorkItem,
  unclaimWorkItem,
} from "./controllers";
import {
  IResearchCompContext,
  IResearchState,
  IResearchWorkItem,
} from "./types";

const replaceWorkItem = (
  state: IResearchState,
  workItem: IResearchWorkItem
) => {
  const index = state.workItems.data.findIndex(
    (item) => item.id === workItem.id
  );
  if (index >= 0) {
    state.workItems.data[index] = workItem;
  }
  if (state.selectedPath.data?.workItem.id === workItem.id) {
    state.selectedPath.data.workItem = workItem;
  }
};

const researchSlice = createSlice({
  name: RESEARCH_SLICE_NAME,
  initialState,
  reducers: {
    clearIntakePreview: (state) => {
      state.intake.preview = undefined;
      state.intake.loadingState = LOADING_STATE.idle;
    },
    clearSelectedPath: (state) => {
      state.selectedPath = initialState.selectedPath;
      state.propertyHistory = initialState.propertyHistory;
    },
    setResearchCompContext: (
      state,
      { payload }: PayloadAction<IResearchCompContext | undefined>
    ) => {
      state.compContext = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResearchReferenceData.pending, (state) => {
        state.referenceData.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchReferenceData.rejected, (state) => {
        state.referenceData.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchReferenceData.fulfilled, (state, { payload }) => {
        state.referenceData.data = payload;
        state.referenceData.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(fetchResearchSummary.pending, (state) => {
        state.summary.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchSummary.rejected, (state) => {
        state.summary.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchSummary.fulfilled, (state, { payload }) => {
        state.summary.data = payload;
        state.summary.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(fetchResearchWorkItems.pending, (state) => {
        state.workItems.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchWorkItems.rejected, (state) => {
        state.workItems.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchWorkItems.fulfilled, (state, { payload }) => {
        state.workItems.data = payload;
        state.workItems.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(fetchResearchClientProgress.pending, (state) => {
        state.clientProgress.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchClientProgress.rejected, (state) => {
        state.clientProgress.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchClientProgress.fulfilled, (state, { payload }) => {
        state.clientProgress.data = payload;
        state.clientProgress.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(fetchResearchWorkItemPath.pending, (state) => {
        state.selectedPath.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchWorkItemPath.rejected, (state) => {
        state.selectedPath.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchWorkItemPath.fulfilled, (state, { payload }) => {
        state.selectedPath.data = payload;
        state.selectedPath.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(fetchResearchPropertyHistory.pending, (state) => {
        state.propertyHistory.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchPropertyHistory.rejected, (state) => {
        state.propertyHistory.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchPropertyHistory.fulfilled, (state, { payload }) => {
        state.propertyHistory.data = payload;
        state.propertyHistory.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(fetchResearchProductivity.pending, (state) => {
        state.productivity.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchProductivity.rejected, (state) => {
        state.productivity.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchProductivity.fulfilled, (state, { payload }) => {
        state.productivity.data = payload;
        state.productivity.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(previewIntake.pending, (state) => {
        state.intake.loadingState = LOADING_STATE.pending;
      })
      .addCase(previewIntake.rejected, (state) => {
        state.intake.loadingState = LOADING_STATE.rejected;
      })
      .addCase(previewIntake.fulfilled, (state, { payload }) => {
        state.intake.preview = payload;
        state.intake.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(submitIntake.pending, (state) => {
        state.intake.loadingState = LOADING_STATE.pending;
      })
      .addCase(submitIntake.rejected, (state) => {
        state.intake.loadingState = LOADING_STATE.rejected;
      })
      .addCase(submitIntake.fulfilled, (state) => {
        state.intake = initialState.intake;
      });

    [
      claimWorkItem,
      unclaimWorkItem,
      transitionWorkItem,
      addWorkItemNote,
      assignWorkItem,
      cancelDemand,
      restoreDemand,
      archiveWorkItem,
      restoreWorkItem,
    ].forEach((action) => {
      builder.addCase(action.fulfilled, (state, { payload }) => {
        replaceWorkItem(state, payload);
      });
    });
  },
});

export const { clearIntakePreview, clearSelectedPath, setResearchCompContext } =
  researchSlice.actions;

export default researchSlice;
